import { useParams, Navigate, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import CTASection from "@/components/sections/CTASection";
import FAQAccordion from "@/components/sections/FAQAccordion";
import Breadcrumbs from "@/components/Breadcrumbs";
import { Reveal, StaggerGroup, staggerItem } from "@/components/motion/Reveal";
import { motion } from "framer-motion";
import { Btn, Eyebrow, SectionHeading } from "@/components/primitives";
import GrowthRing from "@/components/visuals/GrowthRing";
import CampaignHeroVisual from "@/components/visuals/CampaignHeroVisual";
import { getCampaign, campaigns } from "@/content/campaigns";
import { brand } from "@/content/site";
import { AlertCircle, Check, ArrowRight } from "lucide-react";

export default function CampaignLanding() {
  const { slug } = useParams();
  const campaign = getCampaign(slug);
  if (!campaign) return <Navigate to="/404" replace />;

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `Digital growth for ${campaign.audience}`,
    provider: { "@type": "Organization", name: brand.name, url: brand.siteUrl },
    areaServed: "AU",
    description: campaign.subheading,
  };

  const others = campaigns.filter((c) => c.slug !== campaign.slug);

  return (
    <>
      <SEO
        title={`Digital Growth for ${campaign.audience} | ${brand.name}`}
        description={campaign.subheading}
        path={`/for/${campaign.slug}`}
        image={campaign.heroImage}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative bg-white pt-28 md:pt-36 pb-16 md:pb-24 overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-40 -top-24 opacity-70"
        >
          <GrowthRing size={440} duration={80} />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
          <div className="mb-8">
            <Breadcrumbs
              items={[
                { label: "Home", to: "/" },
                { label: "For Tradies", to: "/for" },
                { label: campaign.navLabel, to: `/for/${campaign.slug}` },
              ]}
            />
          </div>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            <div className="lg:col-span-7">
              <Reveal>
                <Eyebrow>{campaign.eyebrow}</Eyebrow>
              </Reveal>
              <Reveal delay={0.05}>
                <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05] text-forest text-balance">
                  {campaign.heading}
                </h1>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-6 text-lg md:text-xl text-mid-neutral leading-relaxed max-w-2xl">
                  {campaign.subheading}
                </p>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="mt-8 flex flex-col sm:flex-row gap-4">
                  <Btn to="/contact" size="lg" data-testid="campaign-primary-cta">
                    Book a Free Growth Assessment
                  </Btn>
                  <Btn to="/plans" variant="secondary" size="lg" withArrow>
                    Compare Growth Plans
                  </Btn>
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <ul className="mt-8 flex flex-wrap gap-2.5">
                  {campaign.trustChips.map((c) => (
                    <li
                      key={c}
                      className="inline-flex items-center gap-2 rounded-full bg-soft border border-border-neutral px-4 py-2 text-sm font-semibold text-forest"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-mint" />
                      {c}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <Reveal delay={0.15}>
                {campaign.heroImage ? (
                  <div className="relative aspect-[4/5] rounded-[3rem] overflow-hidden border-4 border-white shadow-[0_30px_80px_rgba(3,34,26,0.18)]">
                    <img
                      src={campaign.heroImage}
                      alt={campaign.heroAlt}
                      className="h-full w-full object-cover"
                      loading="eager"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-forest/40 via-transparent to-transparent" />
                  </div>
                ) : (
                  <CampaignHeroVisual
                    iconName={campaign.icon}
                    accent={campaign.accent}
                    label={campaign.navLabel}
                  />
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Pain points */}
      <section className="bg-soft py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>Sound familiar?</Eyebrow>
            <SectionHeading className="mt-5">
              What's really slowing your growth down
            </SectionHeading>
            <p className="mt-6 text-lg text-mid-neutral leading-relaxed">
              Every trade has its own version of the same problem: the phone
              rings when you don't want it to, and stays quiet when you do.
              Here's what we see in {campaign.audience.toLowerCase()}.
            </p>
          </div>
          <StaggerGroup className="mt-14 grid md:grid-cols-2 gap-5">
            {campaign.painPoints.map((p) => (
              <motion.div
                key={p.title}
                variants={staggerItem}
                className="rounded-[1.75rem] bg-white border border-border-neutral p-7 md:p-8"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-soft text-mint">
                  <AlertCircle className="h-6 w-6" />
                </div>
                <h3 className="mt-5 text-xl font-extrabold text-forest">
                  {p.title}
                </h3>
                <p className="mt-2 text-mid-neutral leading-relaxed">{p.desc}</p>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* How we help */}
      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow>How {brand.name} helps</Eyebrow>
            <SectionHeading className="mt-5">
              A growth system shaped around {campaign.audience.toLowerCase()}
            </SectionHeading>
            <p className="mt-6 text-lg text-mid-neutral leading-relaxed">
              This isn't a generic marketing package. Every part of the system
              is set up around how your customers actually search, decide and
              buy.
            </p>
          </div>
          <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {campaign.howWeHelp.map((h, i) => (
              <Reveal
                key={h.title}
                delay={(i % 3) * 0.06}
                className="rounded-[1.5rem] border border-border-neutral p-7 hover:border-mint transition-colors"
              >
                <div className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-forest text-lime">
                  <Check className="h-5 w-5" strokeWidth={3} />
                </div>
                <h3 className="mt-5 text-xl font-extrabold text-forest">
                  {h.title}
                </h3>
                <p className="mt-2 text-mid-neutral leading-relaxed">{h.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes — Deep Forest band */}
      <section className="bg-forest py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <div className="max-w-3xl">
            <Eyebrow dark>What changes</Eyebrow>
            <SectionHeading dark className="mt-5">
              Win the business you deserve
            </SectionHeading>
          </div>
          <StaggerGroup className="mt-14 grid md:grid-cols-2 gap-4">
            {campaign.outcomes.map((o) => (
              <motion.div
                key={o}
                variants={staggerItem}
                className="flex items-start gap-4 rounded-2xl bg-white/5 border border-white/10 p-6"
              >
                <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-lime text-forest">
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                </span>
                <p className="text-white/85 leading-relaxed">{o}</p>
              </motion.div>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Cross-link to Installer Growth Pilot — only for solar-netcc-installers */}
      {campaign.slug === "solar-netcc-installers" && (
        <section
          data-testid="solar-installer-crosslink"
          className="bg-white py-16 md:py-24"
        >
          <div className="mx-auto max-w-5xl px-5 sm:px-8">
            <div className="rounded-[2rem] bg-forest text-white p-8 md:p-12 relative overflow-hidden">
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-32 -top-16 opacity-40"
              >
                <GrowthRing size={360} duration={80} />
              </div>
              <div className="relative">
                <Eyebrow dark>For installers ready to build direct sales</Eyebrow>
                <h3 className="mt-5 text-2xl md:text-4xl font-extrabold text-white leading-tight text-balance">
                  Installing for retailers today—but ready to build work under your own brand?
                </h3>
                <p className="mt-5 text-white/80 leading-relaxed max-w-3xl">
                  The Installer Growth Pilot extends Green Circle&apos;s campaign
                  and follow-up support through approved sales closure and
                  installation-ready administration. It is designed for
                  qualified installation businesses that want to test a direct
                  pipeline without abandoning their existing subcontracted work.
                </p>
                <div className="mt-8">
                  <Btn
                    to="/for/installer-growth-pilot"
                    variant="primary"
                    withArrow
                    data-testid="solar-to-installer-cta"
                  >
                    Explore Installer Growth Pilot
                  </Btn>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQs */}
      <section className="bg-soft py-20 md:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <div className="text-center">
            <Eyebrow className="justify-center">Questions</Eyebrow>
            <SectionHeading className="mt-5">
              What {campaign.audience.toLowerCase()} usually ask
            </SectionHeading>
          </div>
          <div className="mt-12">
            <FAQAccordion items={campaign.faqs} emitSchema />
          </div>
        </div>
      </section>

      {/* Cross-links to other campaigns */}
      {others.length > 0 && (
        <section className="bg-white py-16 md:py-20">
          <div className="mx-auto max-w-7xl px-5 sm:px-8">
            <Eyebrow>Also for tradies</Eyebrow>
            <div className="mt-6 grid sm:grid-cols-2 gap-5">
              {others.map((o) => (
                <Link
                  key={o.slug}
                  to={`/for/${o.slug}`}
                  className="group rounded-2xl border border-border-neutral p-6 hover:border-mint transition-colors flex items-center justify-between gap-4"
                >
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-mint">
                      For Tradies
                    </p>
                    <p className="mt-1 text-lg font-extrabold text-forest">
                      {o.audience}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-forest group-hover:translate-x-1 transition-transform" />
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <CTASection
        eyebrow="Free Growth Assessment"
        heading={`Ready to win the ${campaign.audience.toLowerCase()} work you deserve?`}
      />
    </>
  );
}
